import React from "react";
import { format2, getProRataProgress } from "../../helpers/helper";
import { Box, Typography, Skeleton } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { useSalesVsBudget } from "../../hooks/useSalesVsBudget";

function TotalSalesCard({ dateParams, isDateRangeIncomplete }) {
    const {
        data: retData,
        isLoading: retLoading,
        error: retError,
    } = useSalesVsBudget({
        categoryId: "RET",
        dateParams,
        isDateRangeIncomplete,
    });

    const {
        data: prjData,
        isLoading: prjLoading,
        error: prjError,
    } = useSalesVsBudget({
        categoryId: "PRJ",
        dateParams,
        isDateRangeIncomplete,
    });

    if (retLoading || prjLoading) {
        return (
            <Box sx={{ minHeight: 110, p: 2, borderRadius: "20px", bgcolor: "background.paper" }}>
                <Skeleton width="80%" height={16} />
                <Skeleton width="40%" height={28} />
                <Skeleton width="60%" height={14} />
            </Box>
        );
    }

    if (retError || prjError) {
        return (
            <Box sx={{ minHeight: 110, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "background.paper" }}>
                <Typography sx={{ color: "error.main", fontSize: 12 }}>
                    {retError || prjError}
                </Typography>
            </Box>
        );
    }

    if (isDateRangeIncomplete) {
        return (
            <Box
                sx={{
                    minHeight: 110,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                }}
            >
                <Typography sx={{ color: "warning.main", fontSize: 12 }}>
                    Select both From & To dates
                </Typography>
            </Box>
        );
    }

    const totalAchieved = (retData?.achieved || 0) + (prjData?.achieved || 0);
    const totalBudget = (retData?.budget || 0) + (prjData?.budget || 0);

    if (totalAchieved === 0 && totalBudget === 0) {
        return (
            <Box sx={{ minHeight: 110, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "background.paper" }}>
                <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                    No total sales data available
                </Typography>
            </Box>
        );
    }

    const totalAchievedPercent = totalBudget > 0 ? (totalAchieved / totalBudget) * 100 : 0;    
    const { elapsedDays, totalDays } = getProRataProgress(
        dateParams?.filterType,
        dateParams?.to,
        dateParams?.year
    );
    const proRataTarget = totalBudget * (elapsedDays / totalDays);
    const deltaPercent = proRataTarget > 0 ? (totalAchieved / proRataTarget) * 100 : 0;
    const isPositive = deltaPercent >= 100;
    const DeltaIcon = isPositive ? TrendingUpIcon : TrendingDownIcon;
    const deltaColor = isPositive ? "#22C55E" : "#EF4444";

    return (
        <Box
            sx={{
                minHeight: 110,
                p: { xs: 2, lg: 1.5, xl: 2 },
                borderRadius: "20px",
                bgcolor: "background.paper",
                boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                transition: "all 0.2s ease",
                "&:hover": { transform: "translateY(-2px)" },
            }}
        >
            <Typography 
                sx={{ 
                    fontSize: { xs: 12, lg: 11, xl: 13 }, 
                    color: "secondary.main",
                    lineHeight: 1.2,
                    mb: 1
                }}
            >
                Total Sales Achieved Against Budget
            </Typography>

            <Box 
                sx={{ 
                    display: "flex", 
                    alignItems: "flex-end", 
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 0.5
                }}
            >
                <Typography 
                    sx={{ 
                        fontSize: { xs: 22, lg: 16, xl: 18 }, 
                        fontWeight: 700, 
                        color: "text.primary",
                    }}
                >
                    {format2(totalAchievedPercent)} %
                </Typography>

                <Box 
                    sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "2px", 
                        fontSize: { xs: 11, lg: 9, xl: 11 }, 
                        color: deltaColor,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {format2(Math.abs(deltaPercent))} %
                        <DeltaIcon sx={{ fontSize: "inherit", ml: "2px" }} />
                    </Box>
                    <Typography 
                        component="span" 
                        sx={{ 
                            fontSize: "inherit", 
                            color: deltaColor,
                            ml: "2px"
                        }}
                    >
                        vs pro rata
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default React.memo(TotalSalesCard);